import React, { useEffect, useRef, useState } from "react";
import { useDocumentTitle } from "../../../../hooks/customDocumentTitleHook";
import { useLocation, useNavigate } from "react-router-dom";
import { exchangeDate, getPractice } from "../../../../helpers";
import { fetchSingleHipaaConsentForm } from "../../../../services/forms";

export default function HipaaConsentPrint({ ...props }) {
  const {    
    formViewId,
    formViewSize = "A4",
    setFormView = () => {}
  } = props;

  const [formData, setFormData] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const navigate = useNavigate();
  const contentRef = useRef();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const accountId = getPractice("account_id");
  const locationId = getPractice("location_id");

  let formId = formViewId;
  let formSize = formViewSize;
  if (queryParams.get('form_id')) {
    formId = queryParams.get('form_id');
    formSize = queryParams.get('size');
  }  

  useEffect(() => {
    if(formId !== null){
      getReferralData( formId );
    } else {
      navigate(-1);
    }
  }, [formId]);

  const getReferralData = async ( formId ) => {
    const response = await fetchSingleHipaaConsentForm( accountId, locationId, formId );
    if (response) {
      if(response.data === null) {
        window.close();
      }
      setFormData(response?.data);
      setIsDataLoaded(true);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    if (isDataLoaded && queryParams.get('form_id')) {
      handlePrint();
    }
  }, [isDataLoaded, queryParams]);

  const handleCloseWindow = () => {
    window.close();
  };

  const handleFormPrint = (formId) => {
    setFormView();
    const url = `/forms/hipaa-consent-form/print?form_id=${formId}&size=A4`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    const pageSize = formSize == "A4" ? "A4" : "Letter";
    const styleElement = document.createElement("style");
    styleElement.innerHTML = `
      @media print {
        @page {
          size: ${pageSize};
        }
      }
    `;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [formSize]);

  useDocumentTitle("HIPAA Consent Form");

  return (
    <div ref={contentRef} className="docpro_signout_wrap">
      <div className="docpro_signout">
        <div className="row">
          <div className="">
            <h4 style={{ fontWeight: 'bold', fontSize: 18 }} className="text-center mb-0">
              AUTHORIZATION FOR RELEASE OF HEALTH INFORMATION PURSUANT TO HIPAA
            </h4>
            <center className="mt-2 mb-2">
              <span style={{ fontWeight: 'bold', fontSize: 15 }}>
                [This form has been approved by the New York State Department of Health]
              </span>
            </center>
            <ul className="docpro_list_1">
              <li className="flex">
                <p className="mb-0 pe-1 fw-bold" style={{ minWidth: 165 }}>Preferred Location</p>
                <span className="mx-1">:</span>
                <span>{formData?.location_id}</span>
              </li>
              <li className="flex">
                <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 165 }}>Last Name</p>
                <span className="mx-1">:</span>
                <span className="ms-1">{formData?.last_name}</span>
              </li>
              <li className="flex">
                <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 165 }}>First Name</p>
                <span className="mx-1">:</span>
                <span className="ms-1">{formData?.first_name}</span>
              </li>
              <li className="flex">
                <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 165 }}>Date of Birth</p>
                <span className="mx-1">:</span>
                <span>{exchangeDate(formData?.dob)}</span>
              </li>
              <li className="flex">
                <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 165 }}>Social Security Number</p>
                <span className="mx-1">:</span>
                <span>{formData?.ss_number}</span>
              </li>
              <li className="flex">
                <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 165 }}>Patient Address</p>
                <span className="mx-1">:</span>
                <span className="address">{formData?.address}</span>
              </li>
            </ul>
          </div>

          <p style={{ marginTop: 20 }}>
            I, or my authorized representative, request that health information regarding my care and treatment be released as set forth on this form:
          </p>
          <p>
            In accordance with New York State Law and the Privacy Rule of the Health Insurance Portability and Accountability Act of1996(HIPAA), I understand that:
          </p>
          <p>
            <b>1.</b> This authorization may include disclosure of information relating to <b>ALCOHOL DRUG ABUSE, MENTAL HEALTH TREATMENT</b>, except psychotherapy notes, and <b>CONFIDENTIAL HIV* RELATED INFORMATION </b> if I place my initials on the appropriate line in term 9(a). In the event the health information described below includes any of these types of information, and I initial the line on the box in term 9(a), I specifically authorize release of such information to the person(s) indicated in term 8.
          </p>
          <p>
            <b>2.</b> If I am authorizing the release of HIV-related, alcohol or drug treatment, or mental health treatment information, the recipient is prohibited from re disclosing such information without my authorization unless permitted to do so under federal or state law. I understand that I have the right to request a list of people who may receive or use my HIV-related information without authorization. If I experience discrimination because of the release or disclosure of HIV-related information, I may contact the New York State Division of Human Rights at (212) 480-2493 or the New York City Commission of Human Rights at (212) 306-7450. These agencies are responsible for protecting my rights.
          </p>
          <p>
            <b>3.</b> I have the right to revoke this authorization at any time by writing to the health care provider listed below. I understand that I may revoke this authorization except to the extent that action has already been taken based on this authorization.
          </p>
          <p>
            <b>4.</b> I understand that signing this authorization is voluntary. My treatment, payment, enrollment in a health plan, or eligibility for benefits will not be conditioned upon my authorization of this disclosure.
          </p>
          <p>
            <b>5.</b> Information disclosed under this authorization might be re-disclosed by the recipient (except as noted above in term 2), and this re-disclosure may no longer be protected by federal or state law.
          </p>
          <p>
            <b>6.</b> THIS AUTHORIZATION DOES NOT AUTHORIZE YOU TO DISCUSS MY HEALTH INFORMATION OR MEDICAL CARE WITH ANYONE OTHER THAN THE ATTORNEY OR GOVERNMENTAL AGENCY SPECIFIED IN TERM 9 (b).
          </p>

          {formSize == 'A4' ?
            <div className="spacing_4"></div>
          :
            <div className="spacing_12"></div>
          }
          <div className="docpro_table_1 hipaa_table docpro_table_responsive" style={{ boxShadow: 'none', borderRadius: 0, overflowX: 'auto' }}>
            <table className="docpro_mb_0 docpro_primary_color" style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <th>
                    <b>7.</b> Name and address of health provider or entity to release this information:
                  </th>
                </tr>
                <tr>
                  <td>
                    <input type="text" className="table_input" defaultValue={formData?.pro_name_address} readOnly />
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>8.</b> Name and address of person(s) or category of person to whom this information will be sent:
                  </td>
                </tr>
                <tr>
                  <td>
                    <input type="text" className="table_input" defaultValue={formData?.per_name_address} readOnly />
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>9(a).</b> Specific information to be released:
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingTop: '5px', paddingBottom: '5px', borderWidth: '0 1px' }}>
                    <input className="form-check-input" type="checkbox" checked={formData?.medi_record == 1} disabled />
                     Record from (insert date) &nbsp;
                    <span style={{ minWidth: 150, display: 'inline-flex' }}>
                      <span className="w-100" style={{ borderBottom: '1px solid #D3D3D3', display: 'inline-flex', width: 100 }}>{exchangeDate(formData?.form_date)}</span>
                    </span>
                    &nbsp; to (insert date) &nbsp;
                    <span style={{ minWidth: 150, display: 'inline-flex' }}>
                      <span className="w-100" style={{ borderBottom: '1px solid #D3D3D3', display: 'inline-flex', width: 100 }}>{exchangeDate(formData?.to_date)}</span>
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingTop: '5px', paddingBottom: '5px', borderWidth: '0 1px' }} className="cpb-10">
                    <input className="form-check-input" type="checkbox" checked={formData?.entire_medi_rec == 1} disabled />
                     Medical Record, including patient histories, office notes (except psychotherapy notes), test result, radiology studies, films, referrals, consults, billing records, insurance records, and records sent to you by other health care providers
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingTop: '5px', paddingBottom: '5px', borderWidth: '0 1px' }}> <input className="form-check-input" type="checkbox" checked={formData?.other == 1} disabled />Other: </td>
                </tr>
                <tr>
                  <td style={{ paddingBottom: '5px', borderWidth: '0 1px' }}>
                    <span style={{ display: 'inline-block', borderBottom: '1px solid #D3D3D3', width: 300 }}>{formData?.include}</span>
                    &nbsp; Include: (Indicate by Initialing)
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingTop: '5px', paddingBottom: '5px', borderWidth: '0 1px' }}>
                    <span style={{ display: 'inline-block', borderBottom: '1px solid #D3D3D3', width: 300 }}>{formData?.alcohol_drug}</span>
                    <span style={{ marginLeft: '6px', display: 'inline-block', borderBottom: '1px solid #D3D3D3', width: 167 }}>{formData?.treatment}</span>
                    &nbsp; Alcohol/Drug Treatment
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingTop: '5px', paddingBottom: '5px', borderWidth: '0 1px' }}>
                    <span style={{ display: 'inline-block', width: 300 }}></span>
                    <span style={{ marginLeft: '6px', display: 'inline-block', borderBottom: '1px solid #D3D3D3', width: 167 }}>{formData?.mental_info}</span>
                    &nbsp; Mental Health Information
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingTop: '5px', paddingBottom: '5px', borderWidth: '0 1px' }}>
                    <span style={{ display: 'inline-block', width: 300 }}></span>
                    <span style={{ marginLeft: '6px', display: 'inline-block', borderBottom: '1px solid #D3D3D3', width: 167 }}>{formData?.hiv_info}</span>
                    &nbsp; HIV-Related Information
                  </td>
                </tr>

                <tr>
                  <td style={{ paddingTop: '5px', paddingBottom: '5px' }}>9(B). Authorization to Discuss Health Information</td>
                </tr>
                <tr>
                  <td style={{ paddingTop: '5px', paddingBottom: '5px', borderWidth: '0 1px' }}>
                    <input className="form-check-input" type="checkbox" checked={formData?.initialing == 1} disabled />
                     By initialing here &nbsp;
                    <span style={{ minWidth: 150, display: 'inline-flex' }}>
                      <span style={{ borderBottom: '1px solid #D3D3D3', display: 'inline-flex', width: '100%' }}>{formData?.initials_input}</span>
                    </span>
                    &nbsp; I authorize &nbsp;
                    <span style={{ minWidth: 150, display: 'inline-flex' }}>
                      <span style={{ borderBottom: '1px solid #D3D3D3', display: 'inline-flex', width: '100%' }}>{formData?.in_hea_ca_pro}</span>
                    </span>
                    &nbsp; <span>to discuss my health information with my attorney, or a governmental agency, listed here:</span>
                  </td>
                </tr>
                <tr>
                  <td style={{borderWidth: '0 1px' }}>
                    <input type="text" className="table_input" defaultValue={formData?.attorney} readOnly />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="docpro_table_1 hipaa_table docpro_table_responsive" style={{ boxShadow: 'none', marginTop: 38, borderRadius: 0, overflowX: 'auto' }}>
            <table className="docpro_mb_0 docpro_primary_color" style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <th colSpan={2} style={{ textAlign: 'center' }}>
                    (Attorney/Firm Name or Governmental Agency Name)
                  </th>
                </tr>
                <tr>
                  <td style={{ paddingTop: '5px', paddingBottom: '5px', borderRight: '1px solid var(--border)' }}>
                    <b>10.</b> Reason for release of information:
                    <br />
                    <input className="form-check-input" type="checkbox" checked={formData?.individual == 1} disabled />
                     At request of individual <br />
                    <input className="form-check-input" type="checkbox" checked={formData?.individual == 0} disabled />
                     Other: <span style={{ minWidth: 150, display: 'inline-flex' }}><span style={{ borderBottom: '1px solid #D3D3D3', display: 'inline-flex', width: '100%' }}>{formData?.other_2}</span></span>
                  </td>
                  <td style={{ paddingTop: '5px', paddingBottom: '5px' }}>
                    <b>11.</b> Date or event on which this authorization will expire:
                    <br />
                    <span style={{ minWidth: 150, display: 'inline-flex' }}><span style={{ borderBottom: '1px solid #D3D3D3', display: 'inline-flex', width: '100%' }}>{exchangeDate(formData?.auth_exp_date)}</span></span>
                  </td>
                </tr>
                <tr>
                  <td style={{ borderRight: '1px solid var(--border)' }}><b>12.</b> If not the patient, name of person signing form:</td>
                  <td><b>13.</b> Authorization to sign on behalf of patient:</td>
                </tr>
                <tr>
                  <td style={{ borderRight: '1px solid var(--border)' }}>
                    <input type="text" className="table_input" defaultValue={formData?.signing_person} readOnly />
                  </td>
                  <td>
                    <input type="text" className="table_input" defaultValue={formData?.behalf_pe} readOnly />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {formSize == 'A4' ?
            <div className="spacing_15"></div>
          :
            <div className="spacing_7"></div>
          }
          <span style={{ marginTop: 15, marginLeft: 0, paddingLeft: 0, paddingBottom: 10, display: 'inline-block' }}>
            All items on this form have been completed and my questions about this form have been answered. In addition, I have been provided a copy of the form.
          </span>

          <div>
            <ul className="docpro_list_1">
              <li className="list-group-item border-0 flex ps-0">
                <p className="mb-0 pe-2 fw-bold" style={{ minWidth: 108 }}>Your Signature</p><span className="mx-1">:</span>
                <div className="bg-light rounded p-1" style={{ height: 90, width: 177 }}>
                  {formData?.signature_hipaa_image !== null &&
                    <img src={`${formData?.signature_hipaa_image}`} className="img-fluid d-block img_print" alt="signature" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  }
                </div>
              </li>
              <li className="flex">
                <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 108 }}>Date</p><span className="mx-1">:</span>
                <span>{exchangeDate(formData?.dos)}</span>
              </li>
            </ul>
          </div>
          <span style={{ paddingLeft: 0, marginTop: 10, display: 'inline-flex' }}>Signature of patient or representative authorized by law.</span>

          <span style={{ fontWeight: 'bold', padding: 0, marginTop: 25, display: 'inline-flex' }}>
            Human Immunodeficiency Virus that causes AIDS. The New York State Public Health Law protects information which reasonably could identify someone as having HIV symptoms or infection and information regarding a person’s contacts.
          </span>

          <span style={{ margin: '30px 0 20px 0', fontSize: 12, display: 'inline-flex' }}>NYHIPAA 8/09</span>

          <p>
            Instructions for the Use of the HIPAA compliant Authorization Form to Release Health Information Needed for Litigation This form is the product of a collaborative process between the New York State Office of Court Administration, representatives of the medical provider community in New York, and the bench and bar, designed to produce a standard official form that complies with the privacy requirements of the federal Health Insurance Portability and Accountability Act (“HIPAA”) and its implementing regulations, to be used to authorize the release of health information needed for litigation in New York State courts. It can, however, be used more broadly than this and be used before litigation has been commenced, or whenever counsel would find it useful. The goal was to produce a standard HIPAA-compliant official form to obviate the current disputes which often take place as to whether health information requests made in the course of litigation meet the requirements of the HIPAA Privacy Rule. It should be noted, though, that the form is optional. This form maybe filled out on line and downloaded to be signed by hand, or downloaded and filled out entirely on paper. When filing out Item 11, which requests the date or event when the authorization will expire, the person filling out the form may designate an event such as “at the conclusion of my court case” or provide a specific date amount of time, such as “3 years from this date”. If a patient seeks to authorize the release of his or her entire medical record, but only from a certain date, the first two boxes in section 9(a) should both be checked, and the relevant date inserted on the first line containing the first box.
          </p>

          <span style={{ margin: '10px 0 30px 0', fontSize: 12, display: 'inline-flex' }}>NYHIPAA 8/09</span>

          <div className="p-1 mt-3">
            <div className="flex" style={{ alignItems: 'start' }}>
              <input className="form-check-input" type="checkbox" checked={formData?.accept == 1} disabled />
              <label className="form-check-label mb-0" htmlFor="agreement">
                I understand and agree to the above mentioned terms and conditions.
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="docpro_invoice_btns docpro_hide_print">
        <button
          className="docpro_invoice_btn docpro_color3 close_button"
          onClick={() => {
            if (queryParams.get('form_id')) {
              handleCloseWindow();
            } else {
              setFormView();
            }
          }}
        >
          <span className="docpro_btn_icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </span>
          <span className="docpro_btn_text">Close</span>
        </button>

        <button
          className="docpro_invoice_btn docpro_color1"
          onClick={() => {
            if (queryParams.get('form_id')) {
              handlePrint();
            } else {
              handleFormPrint(formViewId);
            }
          }}
        >
          <span className="docpro_btn_icon">
            <svg xmlns="http://www.w3.org/2000/svg" className="ionicon" viewBox="0 0 512 512" >
              <path d="M384 368h24a40.12 40.12 0 0040-40V168a40.12 40.12 0 00-40-40H104a40.12 40.12 0 00-40 40v160a40.12 40.12 0 0040 40h24" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={32} />
              <rect x={128} y={240} width={256} height={208} rx="24.32" ry="24.32" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={32} /> 
              <path d="M384 128v-24a40.12 40.12 0 00-40-40H168a40.12 40.12 0 00-40 40v24" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={32} /> 
              <circle cx={392} cy={184} r={24} fill="currentColor" /> 
            </svg>
          </span>
          <span className="docpro_btn_text">Print</span>
        </button>
      </div>
    </div>
  );
}
